import { useEffect, useState, type FocusEvent } from 'react';
import { Pill, ProgressBar } from '../primitives';
import { Markdown } from '../primitives/Markdown';
import { WysiwygEditor } from '../primitives/WysiwygEditor';
import { parseDod, refCode, statusToPill } from './helpers';
import type { ProjectPatch } from '../../lib/projects/useSaveProject';
import type { ProjectRow } from './types';

export interface ProjectHeaderProps {
  project: ProjectRow;
  leadName: string | null;
  doneCount: number;
  totalCount: number;
  /** Opens the settings sheet. Omit to render a read-only header (Phase-2 behavior). */
  onEditProject?: () => void;
  /**
   * Autosaves an inline edit (title / description) and reloads the canvas.
   * Omit to render a read-only header (Phase-2 behavior).
   */
  onPatch?: (patch: Partial<ProjectPatch>) => Promise<boolean> | void;
}

function formatDate(d: string | null): string {
  if (!d) return 'No target';
  const parsed = new Date(`${d}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? d
    : parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const PencilIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
    <path d="M4 20h4L18 10l-4-4L4 16z" />
    <path d="M13 5l4 4" />
  </svg>
);

export function ProjectHeader({ project, leadName, doneCount, totalCount, onEditProject, onPatch }: ProjectHeaderProps) {
  const dod = parseDod(project.definition_of_done);
  const met = dod.filter((c) => c.met).length;

  // Inline title + description drafts, autosaved on blur. Re-seed from the row
  // whenever it changes externally (the save+reload round-trip lands the same
  // value we typed, so it's a no-op while editing).
  const [nameDraft, setNameDraft] = useState(project.name);
  const [descDraft, setDescDraft] = useState(project.description ?? '');
  useEffect(() => { setNameDraft(project.name); }, [project.name]);
  useEffect(() => { setDescDraft(project.description ?? ''); }, [project.description]);

  const commitName = () => {
    const next = nameDraft.trim();
    if (!next) { setNameDraft(project.name); return; } // never persist an empty title
    if (next !== project.name) void onPatch?.({ name: next });
  };
  const commitDesc = () => {
    if (descDraft !== (project.description ?? '')) void onPatch?.({ description: descDraft });
  };
  const onDescAreaBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    commitDesc();
  };

  return (
    <header className="pn-proj-head" data-testid="project-header">
      <div className="pn-proj-eyebrow">
        <span className="pn-proj-ref">{refCode('PRJ', project.id)}</span>
        <span className="pn-proj-eyebrow-sep" aria-hidden="true">·</span>
        <span className="pn-proj-lock" data-testid="project-header-lock">
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
            <rect x="4" y="10" width="16" height="11" rx="2" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </svg>
          Org-private
        </span>
        {onEditProject ? (
          <button type="button" className="pn-proj-edit" onClick={onEditProject} data-testid="project-header-edit">
            <PencilIcon />
            Edit project
          </button>
        ) : null}
      </div>

      {onPatch ? (
        <>
          <input
            className="pn-proj-title pn-proj-title-input"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.currentTarget.value)}
            onBlur={commitName}
            aria-label="Project name"
            data-testid="project-title-input"
          />
          <div
            className="pn-proj-goal-area"
            data-testid="project-desc-area"
            onBlur={onDescAreaBlur}
          >
            <WysiwygEditor
              value={descDraft}
              onChange={setDescDraft}
              onSave={commitDesc}
              saveState="idle"
              flush
              hideInlineSave
              readonly={!onPatch}
              placeholder="Add a project goal…"
              dataTestId="project-desc"
            />
          </div>
        </>
      ) : (
        <>
          <h1 className="pn-proj-title">{project.name}</h1>
          {project.description ? <Markdown className="pn-proj-goal">{project.description}</Markdown> : null}
        </>
      )}

      <div className="pn-proj-meta">
        {onEditProject ? (
          <button type="button" className="pn-proj-chip pn-proj-chip-btn" onClick={onEditProject} data-testid="project-header-lead">
            <svg className="pn-proj-chip-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <circle cx="12" cy="8" r="3.2" />
              <path d="M5 20a7 7 0 0 1 14 0" />
            </svg>
            Lead <b>{leadName ?? 'Unassigned'}</b>
          </button>
        ) : (
          <span className="pn-proj-chip" data-testid="project-header-lead">
            <svg className="pn-proj-chip-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <circle cx="12" cy="8" r="3.2" />
              <path d="M5 20a7 7 0 0 1 14 0" />
            </svg>
            Lead <b>{leadName ?? 'Unassigned'}</b>
          </span>
        )}
        <Pill status={statusToPill(project.status)}>{project.status}</Pill>
        {onEditProject ? (
          <button type="button" className="pn-proj-chip pn-proj-chip-btn" onClick={onEditProject}>
            <svg className="pn-proj-chip-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <rect x="3" y="5" width="18" height="16" rx="2" />
              <path d="M16 3v4M8 3v4M3 11h18" />
            </svg>
            Target <b>{formatDate(project.target_date)}</b>
          </button>
        ) : (
          <span className="pn-proj-chip">
            <svg className="pn-proj-chip-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <rect x="3" y="5" width="18" height="16" rx="2" />
              <path d="M16 3v4M8 3v4M3 11h18" />
            </svg>
            Target <b>{formatDate(project.target_date)}</b>
          </span>
        )}
        <div className="pn-proj-progress" data-testid="project-header-progress">
          <div className="pn-proj-progress-lbl">
            <span>Progress</span>
            <span><b>{doneCount}</b> / {totalCount} done</span>
          </div>
          <ProgressBar value={doneCount} total={totalCount} label={`${project.name} progress`} />
        </div>
      </div>

      {dod.length > 0 ? (
        <div className="pn-dod-card">
          <div className="pn-dod-head">
            <svg className="pn-dod-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
            <span className="pn-dod-lbl">Definition of Done</span>
            <span className="pn-dod-count" data-testid="project-header-dod-count">{met} / {dod.length} met</span>
            {onEditProject ? (
              <button type="button" className="pn-dod-editbtn" onClick={onEditProject} data-testid="project-header-dod-edit">
                <PencilIcon />
                Edit
              </button>
            ) : null}
          </div>
          <ul className="pn-dod-list">
            {dod.map((c) => (
              <li key={c.id} className={c.met ? 'is-met' : ''}>
                <span className="pn-dod-box" data-met={c.met ? '1' : '0'} aria-hidden="true" />
                <span>{c.text}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </header>
  );
}
